import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { calculateOvertime, formatCurrency, minutesToTime, formatNumber } from '../utils/timeCalculations';
import { getCurrentMonthYear } from '../utils/currentDate';

const HorasExtrasPage = () => {
  const [valorHoraNormal, setValorHoraNormal] = useState('');
  const [horasExtras, setHorasExtras] = useState(0);
  const [minutosExtras, setMinutosExtras] = useState(0);
  const [tipoExtra, setTipoExtra] = useState('50%');
  const [customPercent, setCustomPercent] = useState(50);

  const result = useMemo(() => {
    if (!valorHoraNormal || parseFloat(valorHoraNormal) <= 0) return null;
    
    const valor = parseFloat(valorHoraNormal);
    return calculateOvertime(valor, horasExtras, minutosExtras, tipoExtra, customPercent);
  }, [valorHoraNormal, horasExtras, minutosExtras, tipoExtra, customPercent]);

  const faqs = [
    {
      question: 'Como calcular o valor da hora extra?',
      answer: 'Divida o salário mensal pelo divisor de horas (geralmente 220 para jornada de 44h) para encontrar a hora normal. Depois, aplique o adicional: multiplique por 1,50 para 50% (dias úteis) ou por 2,00 para 100% (domingos e feriados). Multiplique pelo número de horas extras trabalhadas.'
    },
    {
      question: 'Qual o percentual de hora extra nos feriados?',
      answer: 'Nos domingos e feriados, o adicional mínimo de hora extra é de 100% sobre o valor da hora normal quando não há folga compensatória, conforme a Lei 605/1949 (art. 9º) e a Súmula 146 do TST. Ou seja, o trabalhador recebe o dobro do valor da hora normal. Convenções coletivas podem prever percentuais ainda maiores.'
    },
    {
      question: 'Hora extra é obrigatória?',
      answer: 'O empregador não pode obrigar o funcionário a fazer hora extra sem acordo prévio (individual, coletivo ou convenção). A exceção é em caso de necessidade imperiosa (força maior, serviços inadiáveis ou recuperação de tempo perdido), conforme Art. 61 da CLT.'
    },
    {
      question: 'Como calcular DSR sobre hora extra?',
      answer: 'O DSR (Descanso Semanal Remunerado) sobre hora extra é calculado dividindo o total de horas extras do mês pelos dias úteis trabalhados e multiplicando pelos domingos e feriados do mês. Fórmula: DSR = (total HE do mês / dias úteis) x domingos e feriados.'
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Horas Extras CLT — 50% e 100%</title>
        <meta name="description" content="Calcule o valor exato das horas extras conforme a CLT. Adicional de 50% em dias úteis e 100% em domingos e feriados. Resultado instantâneo em R$. Grátis." />
        <meta name="robots" content="index, follow, max-snippet:-1, max-video-preview:-1, max-image-preview:large" />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculadora-de-horas-extras" />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content="Calculadora de Horas Extras CLT — 50% e 100%" />
        <meta property="og:description" content="Calcule o valor exato das horas extras conforme a CLT. Adicional de 50% em dias úteis e 100% em domingos e feriados. Resultado instantâneo em R$. Grátis." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculadora-de-horas-extras" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Horas Extras CLT — 50% e 100%" />
        <meta name="twitter:description" content="Calcule o valor exato das horas extras conforme a CLT. Adicional de 50% em dias úteis e 100% em domingos e feriados. Resultado instantâneo em R$. Grátis." />
        
        <link rel="alternate" hrefLang="pt-BR" href="https://calculadoradehoras.net.br/calculadora-de-horas-extras" />
        <link rel="alternate" hrefLang="x-default" href="https://calculadoradehoras.net.br/calculadora-de-horas-extras" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "WebApplication",
        "name": "Calculadora de Horas Extras CLT",
        "url": "https://calculadoradehoras.net.br/calculadora-de-horas-extras",
        "description": "Calcule o valor das horas extras com adicional de 50% e 100% conforme a CLT. Resultado em R$.",
        "applicationCategory": "UtilityApplication", "operatingSystem": "All",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "BRL" }, "inLanguage": "pt-BR"
      }} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Horas Extras","item":"https://calculadoradehoras.net.br/calculadora-de-horas-extras"}
        ]
      }} />

      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a>
          <span className="mx-2">›</span>
          <span>Horas Extras</span>
        </nav>
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-dark-text mb-4">
            Calculadora de Horas Extras CLT — 50% e 100%
          </h1>
          <p className="text-lg text-body-text">
            Calcule o valor das suas horas extras com adicional de 50% ou 100%
          </p>
          <p className="text-sm text-body-text mt-2" data-testid="last-updated" suppressHydrationWarning>{`Última atualização: ${getCurrentMonthYear()}`}</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-text mb-2">
                Valor da Hora Normal (R$)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={valorHoraNormal}
                onChange={(e) => setValorHoraNormal(e.target.value)}
                placeholder="0.00"
                className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                data-testid="valor-hora-normal-input"
              />
              <p className="mt-1 text-sm text-body-text">
                Divida seu salário mensal pelo divisor de horas (geralmente 220h)
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Horas Extras
                </label>
                <input
                  type="number"
                  min="0"
                  value={horasExtras}
                  onChange={(e) => setHorasExtras(parseInt(e.target.value) || 0)}
                  className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                  data-testid="horas-extras-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-text mb-2">
                  Minutos Extras
                </label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={minutosExtras}
                  onChange={(e) => setMinutosExtras(parseInt(e.target.value) || 0)}
                  className="w-full h-[52px] px-4 border-2 border-border-gray rounded-lg font-mono text-lg focus:outline-none focus:border-blue-primary focus:ring-2 focus:ring-blue-primary"
                  data-testid="minutos-extras-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-text mb-3">
                Tipo de Hora Extra
              </label>
              <div className="space-y-3">
                <label className="flex items-center p-4 border-2 border-border-gray rounded-lg cursor-pointer hover:bg-light-bg transition-colors">
                  <input
                    type="radio"
                    name="tipoExtra"
                    value="50%"
                    checked={tipoExtra === '50%'}
                    onChange={(e) => setTipoExtra(e.target.value)}
                    className="mr-3 w-5 h-5 text-blue-primary"
                    data-testid="tipo-50"
                  />
                  <div>
                    <div className="font-semibold text-dark-text">50% - Dias Úteis</div>
                    <div className="text-sm text-body-text">Hora extra em dias de semana normais</div>
                  </div>
                </label>
                <label className="flex items-center p-4 border-2 border-border-gray rounded-lg cursor-pointer hover:bg-light-bg transition-colors">
                  <input
                    type="radio"
                    name="tipoExtra"
                    value="100%"
                    checked={tipoExtra === '100%'}
                    onChange={(e) => setTipoExtra(e.target.value)}
                    className="mr-3 w-5 h-5 text-blue-primary"
                    data-testid="tipo-100"
                  />
                  <div>
                    <div className="font-semibold text-dark-text">100% - Domingos e Feriados</div>
                    <div className="text-sm text-body-text">Hora extra em domingos ou feriados</div>
                  </div>
                </label>
                <label className="flex items-center p-4 border-2 border-border-gray rounded-lg cursor-pointer hover:bg-light-bg transition-colors">
                  <input
                    type="radio"
                    name="tipoExtra"
                    value="custom"
                    checked={tipoExtra === 'custom'}
                    onChange={(e) => setTipoExtra(e.target.value)}
                    className="mr-3 w-5 h-5 text-blue-primary"
                    data-testid="tipo-custom"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-dark-text mb-2">Percentual Personalizado</div>
                    {tipoExtra === 'custom' && (
                      <input
                        type="number"
                        min="0"
                        max="200"
                        value={customPercent}
                        onChange={(e) => setCustomPercent(parseInt(e.target.value) || 0)}
                        className="w-full h-10 px-3 border-2 border-border-gray rounded-lg font-mono focus:outline-none focus:border-blue-primary"
                        placeholder="50"
                        data-testid="custom-percent-input"
                      />
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>

          {result && (
            <div className="mt-8 bg-blue-primary rounded-xl p-6 text-white">
              <h3 className="text-lg mb-4 opacity-90">Resultado</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>Valor da hora extra:</span>
                  <span className="font-mono font-bold text-xl">{formatCurrency(parseFloat(result.valorHoraExtra))}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Total de horas extras:</span>
                  <span className="font-mono font-bold">{minutesToTime(horasExtras * 60 + minutosExtras)} ({formatNumber(result.totalHorasDecimal)}h)</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Multiplicador:</span>
                  <span className="font-mono font-bold">{result.multiplier}x</span>
                </div>
                <div className="border-t border-white/20 pt-3 mt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg">Total a receber:</span>
                    <span className="font-mono font-bold text-3xl">{formatCurrency(parseFloat(result.valorTotal))}</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-white/20">
                <button
                  onClick={() => {
                    const message = `Horas Extras: ${minutesToTime(horasExtras * 60 + minutosExtras)} (${result.totalHorasDecimal}h) | Valor: ${formatCurrency(parseFloat(result.valorTotal))} | Calculado em https://calculadoradehoras.net.br/calculadora-de-horas-extras`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                  className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                  style={{ backgroundColor: '#25D366' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1fb855'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#25D366'}
                  data-testid="whatsapp-share-button"
                >
                  <svg 
                    className="mr-2" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Compartilhar via WhatsApp
                </button>
              </div>
            </div>
          )}
        </div>

        {/* CTA to Guide Page */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6 md:p-8 mb-8 border-2 border-green-200">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="text-green-600" size={32} />
                <p className="font-bold text-xl font-semibold text-dark-text">
                  Quer entender como funciona?
                </p>
              </div>
              <p className="text-base text-body-text">
                Leia nosso <strong>guia completo sobre cálculo de horas extraordinárias</strong> com exemplos práticos, tabelas de percentuais e explicações detalhadas da CLT.
              </p>
            </div>
            <Link
              to="/calculo-de-horas-extraordinarias"
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Ver Guia Completo
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>

        <AdSlot slotId="ad-slot-1" />

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Calcular o Valor da Hora Extra</h2>
            <p className="text-body-text leading-relaxed mb-4">
              A hora extra é o tempo trabalhado além da jornada normal contratual. No Brasil, a CLT garante que toda hora extra seja remunerada com um adicional sobre o valor da hora normal. Nossa calculadora de horas extras faz esse cálculo automaticamente conforme a legislação.
            </p>
            <p className="text-body-text leading-relaxed mb-4">
              Para calcular suas horas extras:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-body-text mb-4">
              <li>Insira o <strong>valor da sua hora normal</strong> em R$ (divida seu salário mensal pelo divisor de horas, geralmente 220h).</li>
              <li>Informe a <strong>quantidade de horas extras</strong> trabalhadas.</li>
              <li>Selecione o <strong>tipo de hora extra</strong>: 50% para dias úteis ou 100% para domingos e feriados.</li>
            </ol>
            <p className="text-body-text leading-relaxed">
              O resultado mostra o valor da hora extra, o total a receber e o detalhamento do cálculo.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Regras de Horas Extras na CLT</h2>
            <p className="text-body-text leading-relaxed mb-4">
              O <a href="https://www.planalto.gov.br/ccivil_03/decreto-lei/del5452.htm#art59" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Art. 59 da CLT</a> regulamenta as horas extras no Brasil. Veja as principais regras:
            </p>
            <ul className="list-disc list-inside space-y-3 text-body-text mb-4">
              <li><strong>Limite diário:</strong> A jornada pode ser acrescida de no máximo 2 horas extras por dia, mediante acordo individual, convenção coletiva ou acordo coletivo de trabalho.</li>
              <li><strong>Adicional mínimo:</strong> A remuneração da hora extra deve ser pelo menos 50% superior à da hora normal em dias úteis. Em domingos e feriados, o adicional é de 100%. Convenções coletivas podem estabelecer percentuais maiores.</li>
              <li><strong>Base de cálculo:</strong> O adicional de hora extra incide sobre o valor da hora normal, que inclui o salário base e eventuais adicionais de natureza salarial (como insalubridade e periculosidade).</li>
              <li><strong>Limite de jornada:</strong> Com as horas extras, a jornada total não pode ultrapassar 10 horas diárias. Ou seja, 8 horas normais + 2 horas extras.</li>
              <li><strong>Reflexos:</strong> As horas extras habituais integram a remuneração para cálculo de férias, 13º salário, FGTS e DSR (Descanso Semanal Remunerado).</li>
            </ul>
            <p className="text-body-text leading-relaxed">
              Para mais informações sobre direitos trabalhistas e jurisprudência sobre horas extras, consulte o site do <a href="https://www.tst.jus.br/" target="_blank" rel="noopener noreferrer" className="text-blue-primary hover:underline">Tribunal Superior do Trabalho (TST)</a>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Como Calcular: Passo a Passo</h2>
            
            <div className="space-y-4">
              <div className="bg-light-bg p-6 rounded-lg">
                <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">1. Encontre o valor da hora normal</h3>
                <p className="text-body-text leading-relaxed mb-2">
                  Divida o salário mensal pelo divisor de horas. Para jornada de 44h semanais, o divisor é 220.
                </p>
                <p className="text-body-text leading-relaxed">
                  <em>Exemplo:</em> Salário R$ 3.000,00 / 220h = <strong>R$ 13,64 por hora</strong>.
                </p>
              </div>

              <div className="bg-light-bg p-6 rounded-lg">
                <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">2. Aplique o adicional</h3>
                <ul className="list-disc list-inside space-y-2 text-body-text">
                  <li>Para hora extra de 50% (dia útil): R$ 13,64 x 1,50 = <strong>R$ 20,45 por hora extra</strong>.</li>
                  <li>Para hora extra de 100% (domingo/feriado): R$ 13,64 x 2,00 = <strong>R$ 27,27 por hora extra</strong>.</li>
                </ul>
              </div>

              <div className="bg-light-bg p-6 rounded-lg">
                <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">3. Multiplique pela quantidade</h3>
                <p className="text-body-text leading-relaxed">
                  Se você fez 10 horas extras no mês em dias úteis: 10 x R$ 20,45 = <strong>R$ 204,55 de horas extras a receber</strong>.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-heading font-bold text-dark-text mb-4">Exemplos Práticos</h2>
            
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 1: Funcionário com salário mínimo</h3>
              <p className="text-body-text leading-relaxed mb-2">
                Salário mínimo 2026: R$ 1.621,00. Divisor: 220h. Hora normal: R$ 1.621 / 220 = <strong>R$ 7,37</strong>. Hora extra 50%: R$ 7,37 x 1,50 = <strong>R$ 11,05</strong>.
              </p>
              <p className="text-body-text leading-relaxed">
                Se trabalhou 15 horas extras no mês: 15 x R$ 11,05 = <strong>R$ 165,75 a receber</strong>.
              </p>
            </div>

            <div className="bg-light-bg p-6 rounded-lg">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Exemplo 2: Hora extra em feriado</h3>
              <p className="text-body-text leading-relaxed mb-2">
                Funcionário com hora normal de R$ 15,00. Trabalhou 8 horas em um feriado. Hora extra 100%: R$ 15,00 x 2,00 = <strong>R$ 30,00</strong>.
              </p>
              <p className="text-body-text leading-relaxed">
                Total do dia: 8 x R$ 30,00 = <strong>R$ 240,00</strong>.
              </p>
            </div>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-3xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/calculadora-de-horas-extras" />

        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default HorasExtrasPage;
